/*
 * PF for Excel — task pane bridge.
 *
 * Thin Office.js bridge between the LIVE Excel workbook and the Wippy chat
 * that runs in the embedded iframe. Unlike the Outlook add-in, this bridge
 * reads and writes the workbook directly via `Excel.run`: it anchors the
 * agent session on the active sheet/selection/tables, answers read-only
 * inspection queries, and applies structured `ops` (Task B4's normalizer)
 * against the live workbook.
 *
 * Protocol mirrors the Word/Outlook add-ins: a single
 * `{ type: "@office-plugin", action, ... }` envelope, both directions, over
 * postMessage against CHAT_ORIGIN.
 */

/// <reference types="office-js" />

/* global document, Office, Excel, window, console */

import { normalizeOps, freezeSpec } from "./ops";

declare const WIPPY_IFRAME_URL: string;
declare const WIPPY_AGENT_ID: string;

/** Allowed origin for the embedded chat (must match iframe src). */
const CHAT_ORIGIN = new URL(WIPPY_IFRAME_URL).origin;

/**
 * The iframe src. We carry the target agent as an `agent` query param so that
 * whichever surface the iframe points at can honor it:
 *   - the launcher view (excel_editor) hardcodes the same agent and ignores it;
 *   - a generic popup shell can read ?agent=<id> to open that agent directly.
 */
function buildIframeSrc(): string {
  try {
    const u = new URL(WIPPY_IFRAME_URL);
    if (WIPPY_AGENT_ID) u.searchParams.set("agent", WIPPY_AGENT_ID);
    return u.toString();
  } catch {
    return WIPPY_IFRAME_URL;
  }
}

/** Salesforce OAuth popup config — carried over from the Word add-in shell. */
const DEFAULT_REDIRECT_URI = "https://salesforce-office-poc.vercel.app/callback.html";
const DEFAULT_LOGIN_URL = "https://login.salesforce.com";
const SANDBOX_LOGIN_URL = "https://test.salesforce.com";
const LOGIN_PATH = "/services/oauth2/authorize";

let wsReady = false;

Office.onReady((info) => {
  if (info.host !== Office.HostType.Excel) return;

  (document.getElementById("chat-iframe") as HTMLIFrameElement).src = buildIframeSrc();
  document.getElementById("sideload-msg").style.display = "none";
  document.getElementById("app-body").style.display = "flex";

  initExcelBridge();

  // Poll the iframe until its WebSocket to Wippy is ready (== OAuth complete),
  // exactly like the Word/Outlook shells. Once ready we push the workbook context.
  const poll = setInterval(() => {
    if (wsReady) {
      clearInterval(poll);
      return;
    }
    notifyIframe({ type: "@office-plugin", action: "check_ws_status" });
  }, 500);
});

/**
 * Excel Bridge — two-way communication between the workbook and Wippy.
 *
 * Excel → Wippy: on load / sheet activation / selection change we send the
 *   current workbook context as `excel_context` so the agent session can
 *   anchor on it.
 * Wippy → Excel: the agent issues read queries (`read_range`,
 *   `inspect_workbook`, `find_cells`, `get_selection`) and writes
 *   (`apply_ops`) that we execute live against the workbook.
 */
function initExcelBridge(): void {
  Excel.run(async (ctx) => {
    ctx.workbook.worksheets.onActivated.add(() => sendWorkbookContext());
    ctx.workbook.onSelectionChanged?.add?.(() => sendWorkbookContext());
    await ctx.sync();
  }).catch(() => {});

  window.addEventListener("message", (event: MessageEvent) => {
    if (event.origin !== CHAT_ORIGIN) return;

    const data = typeof event.data === "string" ? safeParse(event.data) : event.data;
    if (!data || data.type !== "@office-plugin") return;

    switch (data.action) {
      case "ws_connected":
        // WebSocket connected in the iframe = OAuth complete.
        wsReady = true;
        sendWorkbookContext();
        break;
      case "request_workbook":
        sendWorkbookContext();
        break;
      case "read_range":
        handleReadRange(data);
        break;
      case "inspect_workbook":
        handleInspect(data);
        break;
      case "find_cells":
        handleFind(data);
        break;
      case "get_selection":
        handleGetSelection(data);
        break;
      case "apply_ops":
        handleApplyOps(data);
        break;
      case "open_auth":
        // Optional: let the iframe trigger the Salesforce OAuth popup.
        openAuthDialog();
        break;
    }
  });
}

/** Read the current workbook context (active sheet, sheets, selection, tables) and post it to the iframe. */
async function sendWorkbookContext(): Promise<void> {
  try {
    const payload = await Excel.run(async (ctx) => {
      const sheets = ctx.workbook.worksheets;
      sheets.load("items/name");
      const active = ctx.workbook.worksheets.getActiveWorksheet();
      active.load("name");
      const sel = ctx.workbook.getSelectedRange();
      sel.load("address");
      const tables = ctx.workbook.tables;
      tables.load("items/name");
      await ctx.sync();

      const sheetInfos = [];
      for (const s of sheets.items) {
        const used = s.getUsedRangeOrNullObject(true);
        used.load("address,rowCount,columnCount,isNullObject");
        // eslint-disable-next-line no-await-in-loop
        await ctx.sync();
        sheetInfos.push({
          name: s.name,
          usedRange: used.isNullObject
            ? null
            : { address: used.address, rowCount: used.rowCount, colCount: used.columnCount },
        });
      }
      return {
        activeSheet: active.name,
        sheets: sheetInfos,
        selection: { address: sel.address, sheet: active.name },
        tables: tables.items.map((t) => ({ name: t.name })),
      };
    });
    notifyIframe({ type: "@office-plugin", action: "excel_context", workbook: payload });
  } catch (err: any) {
    console.warn("workbook context failed:", err?.message ?? err);
  }
}

/* ── Read handlers ────────────────────────────────────────────────────────── */

async function handleReadRange(msg: any): Promise<void> {
  const out = await Excel.run(async (ctx) => {
    const sheet = ctx.workbook.worksheets.getItem(msg.sheet);
    const rng = sheet.getRange(msg.range);
    rng.load("address,values,formulas");
    await ctx.sync();
    return { address: rng.address, values: rng.values, formulas: rng.formulas };
  }).catch((e) => ({ error: String(e?.message ?? e) }));
  notifyIframe({ type: "@office-plugin", action: "read_range_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

async function handleGetSelection(msg: any): Promise<void> {
  const out = await Excel.run(async (ctx) => {
    const rng = ctx.workbook.getSelectedRange();
    const sheet = ctx.workbook.worksheets.getActiveWorksheet();
    rng.load("address,values,formulas");
    sheet.load("name");
    await ctx.sync();
    return { address: rng.address, sheet: sheet.name, values: rng.values, formulas: rng.formulas };
  }).catch((e) => ({ error: String(e?.message ?? e) }));
  notifyIframe({ type: "@office-plugin", action: "get_selection_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

async function handleInspect(msg: any): Promise<void> {
  const out = await Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load("items/name,items/visibility");
    const tables = ctx.workbook.tables;
    tables.load("items/name,items/worksheet/name");
    const active = ctx.workbook.worksheets.getActiveWorksheet();
    active.load("name");
    await ctx.sync();

    // Table range addresses (Table has no `.address`; load its range).
    const tableRanges = tables.items.map((t) => {
      const r = t.getRange();
      r.load("address");
      return r;
    });
    await ctx.sync();

    // Per-sheet used-range dimensions + merged/frozen info, so the agent can
    // "see" the structure of an existing (precedent) workbook before editing.
    const sheetInfos: any[] = [];
    for (const s of sheets.items) {
      const used = s.getUsedRangeOrNullObject(true);
      used.load("address,rowCount,columnCount,isNullObject");
      const frozen = s.freezePanes.getLocationOrNullObject();
      frozen.load("address,isNullObject");
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();
      sheetInfos.push({
        name: s.name,
        visibility: s.visibility,
        usedRange: used.isNullObject
          ? null
          : { address: used.address, rowCount: used.rowCount, colCount: used.columnCount },
        frozenAt: frozen.isNullObject ? null : frozen.address,
      });
    }
    return {
      activeSheet: active.name,
      sheets: sheetInfos,
      tables: tables.items.map((t, i) => ({ name: t.name, sheet: t.worksheet?.name, range: tableRanges[i]?.address })),
    };
  }).catch((e) => ({ error: String(e?.message ?? e) }));
  notifyIframe({ type: "@office-plugin", action: "inspect_workbook_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

async function handleFind(msg: any): Promise<void> {
  const q = String(msg.query ?? "");
  const out = await Excel.run(async (ctx) => {
    const sheets = ctx.workbook.worksheets;
    sheets.load("items/name");
    await ctx.sync();
    const matches: any[] = [];
    for (const s of sheets.items) {
      if (msg.sheet && s.name !== msg.sheet) continue;
      const found = s.findAllOrNullObject(q, { completeMatch: false, matchCase: false });
      found.load("areas/items/address,areas/items/values,isNullObject");
      // eslint-disable-next-line no-await-in-loop
      await ctx.sync();
      if (!found.isNullObject) {
        for (const a of found.areas.items) matches.push({ sheet: s.name, address: a.address, value: a.values?.[0]?.[0] });
      }
    }
    return { matches };
  }).catch((e) => ({ error: String(e?.message ?? e), matches: [] }));
  notifyIframe({ type: "@office-plugin", action: "find_cells_result", request_id: msg.request_id, reply_pid: msg.reply_pid, ...out });
}

/* ── Write handler ────────────────────────────────────────────────────────── */

// Named styles persist across apply_ops calls — define once with {op:"style",id:...}
// and reference by id on later ops, even in a separate apply_ops call.
const namedStyles: Record<string, any> = {};

// Resolve the style to apply for an op: a string is a named-style id; an object is
// an inline style (the agent naturally passes style:{bold:true,...}); undefined
// otherwise. Top-level style props on the op (e.g. {op:"cell",bold:true}) are
// applied separately by applyStyle(range, o).
function resolveStyle(o: any, styles: Record<string, any>): any {
  if (typeof o.style === "string") return styles[o.style];
  if (o.style && typeof o.style === "object") return o.style;
  return undefined;
}

async function handleApplyOps(msg: any): Promise<void> {
  const norm = normalizeOps(msg.ops);
  if (norm.ok === false) {
    notifyIframe({
      type: "@office-plugin",
      action: "apply_ops_result",
      request_id: msg.request_id,
      reply_pid: msg.reply_pid,
      applied: 0,
      formula_errors: [],
      skipped: [],
      error: `op #${norm.index}: ${norm.message}`,
    });
    return;
  }
  const styles = namedStyles;
  const commentOps: any[] = [];
  const result = await Excel.run(async (ctx) => {
    let applied = 0;
    let styleDefs = 0;
    const skipped: Array<{ op: string; sheet?: string; ref?: string; reason?: string; index?: number }> = [];
    const touched: { sheet: string; address: string }[] = [];

    // Record a warning (in `skipped`) when an op references a named style id that
    // was never defined — so a bad/typo'd/cross-call style ref is never silent.
    const warnUnresolvedStyle = (o: any, opName: string, ref?: string) => {
      if (typeof o.style === "string" && !namedStyles[o.style]) {
        skipped.push({
          op: opName,
          sheet: o.sheet,
          ref,
          reason: `named style '${o.style}' is not defined — define {op:"style",id:"${o.style}",…} in this same call, or use an inline style object`,
        });
      }
    };

    // An op may omit `sheet` to target the most-recently-selected sheet, exactly
    // as the server ops-log does. Track the current sheet, seeded to the active one.
    const active = ctx.workbook.worksheets.getActiveWorksheet();
    active.load("name");
    await ctx.sync();
    let currentSheet: string = active.name;
    // Worksheets we create/copy this call — targeting a just-added sheet by name
    // can fail before it commits, so we reuse the worksheet object directly.
    const sheetCache: Record<string, Excel.Worksheet> = {};
    const resolveSheet = (o: any): string => {
      if (o.sheet) currentSheet = o.sheet;
      return currentSheet;
    };
    const sheetOf = (name: string): Excel.Worksheet =>
      sheetCache[name] ?? ctx.workbook.worksheets.getItemOrNullObject(name);

    // Each op is committed on its own sync, so (a) a sheet created earlier in the
    // batch exists before later ops target it, (b) one bad op is isolated with its
    // index + reason instead of failing the whole call, and (c) clean ops still land.
    for (let i = 0; i < norm.ops.length; i++) {
      const o = norm.ops[i] as any;
      const beforeApplied = applied;
      const beforeStyleDefs = styleDefs;
      const beforeTouched = touched.length;
      try {
        switch (o.op) {
        case "style":
          // Register a reusable named style (persists across apply_ops calls).
          // Counted separately (style_defs), not in `applied` cell-write total.
          styles[o.id] = o;
          styleDefs++;
          break;
        case "sheet": {
          let s = ctx.workbook.worksheets.getItemOrNullObject(o.name);
          s.load("isNullObject");
          await ctx.sync();
          if (s.isNullObject) s = ctx.workbook.worksheets.add(o.name);
          sheetCache[o.name] = s;
          currentSheet = o.name;
          applied++;
          break;
        }
        case "rename_sheet":
          sheetOf(o.from).name = o.to;
          if (currentSheet === o.from) currentSheet = o.to;
          applied++;
          break;
        case "delete_sheet":
          sheetOf(o.name).delete();
          applied++;
          break;
        case "cell": {
          const sh = resolveSheet(o);
          const r = sheetOf(sh).getRange(o.ref);
          const named = resolveStyle(o, styles); // named id, inline object, or undefined
          warnUnresolvedStyle(o, "cell", o.ref);
          // Apply a text/number format BEFORE writing the value, so text that looks
          // like a date or fraction ("6/30") is not coerced into a date serial.
          const nf = o.number_format ?? named?.number_format;
          if (nf) r.numberFormat = [[nf]];
          // Only write content when supplied — a style-only op must NOT clear the cell.
          const hasContent = o.formula !== undefined || o.value !== undefined;
          if (o.formula !== undefined) r.formulas = [[o.formula]];
          else if (o.value !== undefined) r.values = [[o.value]];
          if (named) applyStyle(r, named);
          applyStyle(r, o); // inline props override the named style
          if (hasContent) touched.push({ sheet: sh, address: o.ref });
          applied++;
          break;
        }
        case "rows": {
          const sh = resolveSheet(o);
          const r = sheetOf(sh)
            .getRange(o.at)
            .getResizedRange(o.rows.length - 1, (o.rows[0]?.length ?? 1) - 1);
          r.values = o.rows;
          const rowsStyle = resolveStyle(o, styles);
          warnUnresolvedStyle(o, "rows", o.at);
          if (rowsStyle) applyStyle(r, rowsStyle);
          applyStyle(r, o);
          const rowsNf = o.number_format ?? rowsStyle?.number_format;
          if (rowsNf) {
            const rc = o.rows.length;
            const cc = o.rows[0]?.length ?? 1;
            r.numberFormat = Array.from({ length: rc }, () => Array.from({ length: cc }, () => rowsNf));
          }
          r.load("address");
          await ctx.sync();
          touched.push({ sheet: sh, address: r.address });
          applied++;
          break;
        }
        case "merge":
          sheetOf(resolveSheet(o)).getRange(o.range).merge(o.across ?? false);
          applied++;
          break;
        case "unmerge":
          sheetOf(resolveSheet(o)).getRange(o.range).unmerge();
          applied++;
          break;
        case "clear": {
          // Default clears contents; honor explicit contents/formats flags.
          const applyTo =
            o.formats && o.contents === false
              ? Excel.ClearApplyTo.formats
              : o.formats
              ? Excel.ClearApplyTo.all
              : Excel.ClearApplyTo.contents;
          sheetOf(resolveSheet(o)).getRange(o.range).clear(applyTo);
          applied++;
          break;
        }
        case "freeze": {
          // o.ref is the first UNFROZEN cell (Excel UI convention). freezeAt()
          // treats its argument as the frozen region instead, so passing a
          // mid-sheet cell like "A4" strands the header rows above the split
          // where they cannot be scrolled to. Translate to explicit row/column
          // counts, which freezeRows/freezeColumns interpret unambiguously.
          const sheet = sheetOf(resolveSheet(o));
          const spec = freezeSpec(o.ref);
          if (!spec) {
            sheet.freezePanes.freezeAt(sheet.getRange(o.ref));
          } else if (spec.rows > 0 && spec.cols > 0) {
            // Freeze the A1-anchored header block (rows AND columns).
            const colName = (n: number): string => {
              let s = "";
              while (n > 0) {
                s = String.fromCharCode(65 + ((n - 1) % 26)) + s;
                n = Math.floor((n - 1) / 26);
              }
              return s;
            };
            sheet.freezePanes.freezeAt(sheet.getRange(`A1:${colName(spec.cols)}${spec.rows}`));
          } else if (spec.rows > 0) {
            sheet.freezePanes.freezeRows(spec.rows);
          } else if (spec.cols > 0) {
            sheet.freezePanes.freezeColumns(spec.cols);
          }
          // spec.rows === 0 && spec.cols === 0 (ref was "A1"): nothing to freeze.
          applied++;
          break;
        }
        case "col": {
          // Op `width` is an Excel character-width (openpyxl); Office.js columnWidth
          // is in points — approximate chars→points (~5.5pt/char).
          sheetOf(resolveSheet(o)).getRange(`${o.col}:${o.col}`).format.columnWidth =
            Math.round(Number(o.width) * 5.5);
          applied++;
          break;
        }
        case "row":
          // Row height is in points on both sides — pass through.
          sheetOf(resolveSheet(o)).getRange(`${o.row}:${o.row}`).format.rowHeight = Number(o.height);
          applied++;
          break;
        case "autofit_cols": {
          const sh = resolveSheet(o);
          const r = o.range ? sheetOf(sh).getRange(o.range) : sheetOf(sh).getUsedRangeOrNullObject(true);
          r.format.autofitColumns();
          applied++;
          break;
        }
        case "autofit_rows": {
          const sh = resolveSheet(o);
          const r = o.range ? sheetOf(sh).getRange(o.range) : sheetOf(sh).getUsedRangeOrNullObject(true);
          r.format.autofitRows();
          applied++;
          break;
        }
        case "insert_rows": {
          const end = Number(o.at) + Number(o.count) - 1;
          sheetOf(resolveSheet(o)).getRange(`${o.at}:${end}`).insert(Excel.InsertShiftDirection.down);
          applied++;
          break;
        }
        case "delete_rows": {
          const end = Number(o.at) + Number(o.count) - 1;
          sheetOf(resolveSheet(o)).getRange(`${o.at}:${end}`).delete(Excel.DeleteShiftDirection.up);
          applied++;
          break;
        }
        case "insert_cols": {
          const [c1, c2] = colSpan(o.at, o.count);
          sheetOf(resolveSheet(o)).getRange(`${c1}:${c2}`).insert(Excel.InsertShiftDirection.right);
          applied++;
          break;
        }
        case "delete_cols": {
          const [c1, c2] = colSpan(o.at, o.count);
          sheetOf(resolveSheet(o)).getRange(`${c1}:${c2}`).delete(Excel.DeleteShiftDirection.left);
          applied++;
          break;
        }
        case "conditional_format": {
          applyConditionalFormat(sheetOf(resolveSheet(o)).getRange(o.range), o.rule || {});
          applied++;
          break;
        }
        case "chart": {
          const sheet = sheetOf(resolveSheet(o));
          const seriesBy =
            o.series_by === "rows"
              ? Excel.ChartSeriesBy.rows
              : o.series_by === "columns"
              ? Excel.ChartSeriesBy.columns
              : Excel.ChartSeriesBy.auto;
          const chart = sheet.charts.add(chartTypeOf(o.chart_type), sheet.getRange(o.data_range), seriesBy);
          if (o.title) chart.title.text = String(o.title);
          if (o.position) {
            const p = String(o.position);
            if (p.includes(":")) {
              const [s, e] = p.split(":");
              chart.setPosition(s, e);
            } else {
              chart.setPosition(p);
            }
          }
          applied++;
          break;
        }
        case "data_validation": {
          applyDataValidation(sheetOf(resolveSheet(o)).getRange(o.range), o.rule || {});
          applied++;
          break;
        }
        case "copy_sheet": {
          const src = sheetOf(o.from);
          const copied = src.copy(Excel.WorksheetPositionType.after, src);
          if (o.to) {
            copied.name = o.to;
            sheetCache[o.to] = copied;
          }
          applied++;
          break;
        }
        case "copy_range": {
          const fromSheet = sheetOf(o.from_sheet || resolveSheet(o));
          const toSheet = sheetOf(o.to_sheet || o.from_sheet || currentSheet);
          const copyType =
            o.type === "values"
              ? Excel.RangeCopyType.values
              : o.type === "formulas"
              ? Excel.RangeCopyType.formulas
              : o.type === "formats"
              ? Excel.RangeCopyType.formats
              : Excel.RangeCopyType.all;
          toSheet.getRange(o.to).copyFrom(fromSheet.getRange(o.from), copyType);
          applied++;
          break;
        }
        case "create_table": {
          const t = sheetOf(resolveSheet(o)).tables.add(o.range, o.has_headers !== false);
          if (o.name) t.name = o.name;
          if (o.style) t.style = o.style;
          applied++;
          break;
        }
        case "pivot": {
          // source/dest are sheet-qualified ("Data!A1:F500"). Office.js wants a Range
          // for each. A pivot cannot be added onto its own source sheet region, and the
          // destination sheet must already exist — the agent creates it with a `sheet` op.
          const splitQualified = (q: string): [string, string] => {
            const bang = q.lastIndexOf("!");
            if (bang < 0) throw new Error(`pivot needs a sheet-qualified ref, got "${q}"`);
            return [q.slice(0, bang).replace(/^'|'$/g, ""), q.slice(bang + 1)];
          };
          const [srcSheet, srcAddr] = splitQualified(o.source);
          const [dstSheet, dstAddr] = splitQualified(o.dest);
          const sourceRange = sheetOf(srcSheet).getRange(srcAddr);
          const destRange = sheetOf(dstSheet).getRange(dstAddr);

          const name = o.name ?? `Pivot_${dstSheet}_${dstAddr}`.replace(/[^A-Za-z0-9_]/g, "_");
          const pt = ctx.workbook.pivotTables.add(name, sourceRange, destRange);

          for (const f of o.rows ?? []) pt.rowHierarchies.add(pt.hierarchies.getItem(f));
          for (const f of o.cols ?? []) pt.columnHierarchies.add(pt.hierarchies.getItem(f));
          for (const f of o.filters ?? []) pt.filterHierarchies.add(pt.hierarchies.getItem(f));
          for (const v of o.values ?? []) {
            const dh = pt.dataHierarchies.add(pt.hierarchies.getItem(v.field));
            dh.summarizeBy = (v.fn ?? "Sum") as Excel.AggregationFunction;
          }
          applied++;
          break;
        }
        case "hyperlink": {
          const r = sheetOf(resolveSheet(o)).getRange(o.ref);
          const hl: Excel.RangeHyperlink = {};
          if (o.url) hl.address = o.url;
          if (o.target_ref) hl.documentReference = o.target_ref;
          if (o.text) hl.textToDisplay = o.text;
          if (o.tooltip) hl.screenTip = o.tooltip;
          r.hyperlink = hl;
          applied++;
          break;
        }
        case "format": {
          const rng = sheetOf(resolveSheet(o)).getRange(o.range);
          const fmtStyle = resolveStyle(o, styles);
          warnUnresolvedStyle(o, "format", o.range);
          if (fmtStyle) applyStyle(rng, fmtStyle);
          applyStyle(rng, o);
          const fmtNf = o.number_format ?? fmtStyle?.number_format;
          if (fmtNf) {
            rng.load("rowCount,columnCount");
            // eslint-disable-next-line no-await-in-loop
            await ctx.sync();
            rng.numberFormat = Array.from({ length: rng.rowCount }, () =>
              Array.from({ length: rng.columnCount }, () => fmtNf)
            );
          }
          applied++;
          break;
        }
        case "sort": {
          const keys = Array.isArray(o.by) ? o.by : [o.by];
          const ascending = o.order !== "desc" && o.order !== "descending";
          const fields = keys.map((k: any) => ({ key: Number(k), ascending }));
          sheetOf(resolveSheet(o)).getRange(o.range).sort.apply(fields, false, o.has_headers ?? true);
          applied++;
          break;
        }
        case "autofilter": {
          const sh2 = sheetOf(resolveSheet(o));
          sh2.autoFilter.apply(sh2.getRange(o.range));
          applied++;
          break;
        }
        case "define_name": {
          const nsheet = o.sheet || currentSheet;
          ctx.workbook.names.add(o.name, `'${nsheet}'!${o.range}`);
          applied++;
          break;
        }
        case "comment":
          // The Office.js comments API throws when a cell already has a comment,
          // so collect comment ops and apply them AFTER the main batch in isolated
          // runs — a comment failure must never corrupt the rest of the write.
          commentOps.push(o);
          break;
        default:
          skipped.push({ op: o.op, sheet: o.sheet });
          break;
        }
        // Commit this op on its own so a bad op is isolated (not fatal to the batch).
        await ctx.sync();
      } catch (e) {
        applied = beforeApplied;
        styleDefs = beforeStyleDefs;
        touched.length = beforeTouched;
        skipped.push({
          op: o.op,
          index: i,
          sheet: o.sheet,
          ref: o.ref ?? o.range ?? o.at ?? o.name,
          reason: String((e as any)?.message ?? e),
        });
      }
    }

    // Read back formula errors on the touched ranges.
    const errs: any[] = [];
    for (const t of touched) {
      try {
        const r = ctx.workbook.worksheets.getItem(t.sheet).getRange(t.address);
        r.load("values,address");
        // eslint-disable-next-line no-await-in-loop
        await ctx.sync();
        (r.values as any[][]).forEach((row, ri) =>
          row.forEach((v, ci) => {
            if (typeof v === "string" && /^#(REF|DIV\/0|VALUE|NAME\?|N\/A|NUM|NULL)!?/.test(v))
              errs.push({ sheet: t.sheet, address: t.address, cell: [ri, ci], error: v });
          })
        );
      } catch {
        /* formula-error readback is best-effort */
      }
    }
    return { applied, style_defs: styleDefs, formula_errors: errs, skipped };
  }).catch((e) => ({ applied: 0, style_defs: 0, formula_errors: [], skipped: [], error: String(e?.message ?? e) }));

  // Apply any comment ops, each in its own Excel.run so a failure is isolated.
  // Only when the main batch succeeded.
  const res = result as any;
  if (!res.error && commentOps.length > 0) {
    let activeName = "";
    await Excel.run(async (ctx) => {
      const a = ctx.workbook.worksheets.getActiveWorksheet();
      a.load("name");
      await ctx.sync();
      activeName = a.name;
    }).catch(() => {});
    for (const c of commentOps) {
      const sheetName = c.sheet || activeName;
      const text = String(c.text ?? "");
      // Try to add; if the cell already has a comment, replace it.
      // eslint-disable-next-line no-await-in-loop
      const ok = await Excel.run(async (ctx) => {
        ctx.workbook.worksheets.getItem(sheetName).comments.add(c.ref, text);
        await ctx.sync();
        return true;
      }).catch(() =>
        Excel.run(async (ctx) => {
          const cs = ctx.workbook.worksheets.getItem(sheetName).comments;
          cs.getItemByCell(c.ref).delete();
          cs.add(c.ref, text);
          await ctx.sync();
          return true;
        }).catch(() => false)
      );
      if (ok) {
        res.applied = (res.applied || 0) + 1;
      } else {
        if (!res.skipped) res.skipped = [];
        res.skipped.push({ op: "comment", sheet: sheetName });
      }
    }
  }

  notifyIframe({
    type: "@office-plugin",
    action: "apply_ops_result",
    request_id: msg.request_id,
    reply_pid: msg.reply_pid,
    ...result,
  });
}

const toHex = (c: string): string => {
  const s = String(c).trim();
  return s.startsWith("#") ? s : `#${s}`;
};
const cap = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

function applyStyle(range: Excel.Range, style: any): void {
  if (!style) return;
  const f = range.format;
  if (style.bold !== undefined) f.font.bold = style.bold;
  if (style.italic !== undefined) f.font.italic = style.italic;
  if (style.wrap !== undefined) f.wrapText = style.wrap;
  if (style.font) f.font.name = style.font;
  if (style.size) f.font.size = Number(style.size);
  if (style.font_color) f.font.color = toHex(style.font_color);
  if (style.fill) f.fill.color = toHex(style.fill);
  if (style.align) f.horizontalAlignment = cap(style.align) as Excel.HorizontalAlignment;
  if (style.valign) f.verticalAlignment = cap(style.valign) as Excel.VerticalAlignment;
  // number_format is NOT set here — it is a per-cell 2D array that does not broadcast
  // to a multi-cell range, so each op sets it with correctly-sized dimensions.
  // Borders: `border` sets all edges; `border_top/bottom/left/right` set one edge.
  // Each value is "thin" | "medium" | "thick" | "double" (or true = thin). Total
  // rows use border_top; header underlines use border_bottom.
  const setBorder = (edge: string, spec: unknown): void => {
    const s = spec === true ? "thin" : String(spec);
    const b = f.borders.getItem(edge as Excel.BorderIndex);
    b.style = (s === "double" ? "Double" : "Continuous") as Excel.BorderLineStyle;
    b.weight = (s === "thick" ? "Thick" : s === "medium" ? "Medium" : "Thin") as Excel.BorderWeight;
  };
  if (style.border) for (const e of ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight"]) setBorder(e, style.border);
  if (style.border_top) setBorder("EdgeTop", style.border_top);
  if (style.border_bottom) setBorder("EdgeBottom", style.border_bottom);
  if (style.border_left) setBorder("EdgeLeft", style.border_left);
  if (style.border_right) setBorder("EdgeRight", style.border_right);
}

// Column-index helpers for structural insert_cols/delete_cols, whose `at` may be
// a column letter ("C") or a 1-based index.
const colNumToLetter = (n: number): string => {
  let s = "";
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
};
const colLetterToNum = (letter: string): number => {
  let n = 0;
  for (const ch of letter.toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n;
};
function colSpan(at: string | number, count: number): [string, string] {
  const start = typeof at === "number" || /^\d+$/.test(String(at)) ? Number(at) : colLetterToNum(String(at));
  const end = start + Number(count) - 1;
  return [colNumToLetter(start), colNumToLetter(end)];
}

/* ── Chart / conditional-format / data-validation helpers ─────────────────── */

function chartTypeOf(t: string): Excel.ChartType {
  const map: Record<string, Excel.ChartType> = {
    column: Excel.ChartType.columnClustered,
    columnClustered: Excel.ChartType.columnClustered,
    columnStacked: Excel.ChartType.columnStacked,
    bar: Excel.ChartType.barClustered,
    barStacked: Excel.ChartType.barStacked,
    line: Excel.ChartType.line,
    lineMarkers: Excel.ChartType.lineMarkers,
    pie: Excel.ChartType.pie,
    doughnut: Excel.ChartType.doughnut,
    area: Excel.ChartType.area,
    areaStacked: Excel.ChartType.areaStacked,
    scatter: Excel.ChartType.xyscatter,
    xyscatter: Excel.ChartType.xyscatter,
  };
  return map[String(t)] ?? Excel.ChartType.columnClustered;
}

function cfFormatApply(cfFormat: Excel.ConditionalRangeFormat | undefined, fmt: any): void {
  if (!cfFormat || !fmt) return;
  if (fmt.bold !== undefined) cfFormat.font.bold = fmt.bold;
  if (fmt.italic !== undefined) cfFormat.font.italic = fmt.italic;
  if (fmt.font_color) cfFormat.font.color = toHex(fmt.font_color);
  if (fmt.fill) cfFormat.fill.color = toHex(fmt.fill);
}

function cellValueOperator(op: string): Excel.ConditionalCellValueOperator {
  const m: Record<string, Excel.ConditionalCellValueOperator> = {
    lessThan: Excel.ConditionalCellValueOperator.lessThan,
    lessThanOrEqual: Excel.ConditionalCellValueOperator.lessThanOrEqual,
    greaterThan: Excel.ConditionalCellValueOperator.greaterThan,
    greaterThanOrEqual: Excel.ConditionalCellValueOperator.greaterThanOrEqual,
    equalTo: Excel.ConditionalCellValueOperator.equalTo,
    notEqualTo: Excel.ConditionalCellValueOperator.notEqualTo,
    between: Excel.ConditionalCellValueOperator.between,
    notBetween: Excel.ConditionalCellValueOperator.notBetween,
  };
  return m[String(op)] ?? Excel.ConditionalCellValueOperator.lessThan;
}

// rule: { type, operator?, formula1?, formula2?, text?, rank?, percent?, color?, format:{bold,fill,font_color} }
function applyConditionalFormat(range: Excel.Range, rule: any): void {
  const type = String(rule.type || "cellValue");
  if (type === "cellValue") {
    const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.cellValue);
    const op = cellValueOperator(rule.operator);
    cf.cellValue.rule =
      rule.formula2 !== undefined
        ? { formula1: String(rule.formula1), formula2: String(rule.formula2), operator: op }
        : ({ formula1: String(rule.formula1), operator: op } as Excel.ConditionalCellValueRule);
    cfFormatApply(cf.cellValue.format, rule.format);
  } else if (type === "containsText" || type === "text") {
    const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.containsText);
    cf.textComparison.rule = { operator: Excel.ConditionalTextOperator.contains, text: String(rule.text ?? "") };
    cfFormatApply(cf.textComparison.format, rule.format);
  } else if (type === "colorScale") {
    const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.colorScale);
    cf.colorScale.criteria = {
      minimum: { color: "#F8696B", type: Excel.ConditionalFormatColorCriterionType.lowestValue },
      midpoint: { color: "#FFEB84", formula: "50", type: Excel.ConditionalFormatColorCriterionType.percent },
      maximum: { color: "#63BE7B", type: Excel.ConditionalFormatColorCriterionType.highestValue },
    };
  } else if (type === "dataBar") {
    const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.dataBar);
    if (rule.color) cf.dataBar.positiveFormat.fillColor = toHex(rule.color);
  } else if (type === "top" || type === "bottom") {
    const cf = range.conditionalFormats.add(Excel.ConditionalFormatType.topBottom);
    const percent = !!rule.percent;
    cf.topBottom.rule = {
      rank: Number(rule.rank ?? 10),
      type:
        type === "top"
          ? percent
            ? Excel.ConditionalTopBottomCriterionType.topPercent
            : Excel.ConditionalTopBottomCriterionType.topItems
          : percent
          ? Excel.ConditionalTopBottomCriterionType.bottomPercent
          : Excel.ConditionalTopBottomCriterionType.bottomItems,
    };
    cfFormatApply(cf.topBottom.format, rule.format);
  }
}

// rule: { type:"list", values:[...] | source:"A1:A5" } OR
//       { type:"whole"|"decimal"|"date"|"textLength", operator, formula1, formula2? }
function applyDataValidation(range: Excel.Range, rule: any): void {
  const type = String(rule.type || "list");
  if (type === "list") {
    const source = Array.isArray(rule.values) ? rule.values.join(",") : String(rule.source ?? "");
    range.dataValidation.rule = { list: { inCellDropDown: rule.dropdown !== false, source } };
    return;
  }
  const opMap: Record<string, Excel.DataValidationOperator> = {
    between: Excel.DataValidationOperator.between,
    notBetween: Excel.DataValidationOperator.notBetween,
    greaterThan: Excel.DataValidationOperator.greaterThan,
    greaterThanOrEqualTo: Excel.DataValidationOperator.greaterThanOrEqualTo,
    lessThan: Excel.DataValidationOperator.lessThan,
    lessThanOrEqualTo: Excel.DataValidationOperator.lessThanOrEqualTo,
    equalTo: Excel.DataValidationOperator.equalTo,
    notEqualTo: Excel.DataValidationOperator.notEqualTo,
  };
  const criteria: any = {
    operator: opMap[String(rule.operator)] ?? Excel.DataValidationOperator.between,
    formula1: String(rule.formula1),
  };
  if (rule.formula2 !== undefined) criteria.formula2 = String(rule.formula2);
  const key = type === "textLength" ? "textLength" : type;
  range.dataValidation.rule = { [key]: criteria } as Excel.DataValidationRule;
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

function safeParse(s: string): any {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function notifyIframe(message: Record<string, unknown>): void {
  const iframe = document.getElementById("chat-iframe") as HTMLIFrameElement;
  iframe?.contentWindow?.postMessage(message, CHAT_ORIGIN);
}

/**
 * Salesforce OAuth popup — carried over verbatim from the Word add-in shell.
 * Invoked on demand when the iframe sends `open_auth`. The iframe otherwise
 * drives its own auth and signals completion via `ws_connected`.
 */
function openAuthDialog(): void {
  const urlParams = new URLSearchParams(window.location.search);
  const clientId = urlParams.get("clientId") || "";
  const redirectUri = urlParams.get("redirectUri") || DEFAULT_REDIRECT_URI;
  const isSandbox = urlParams.get("isSandbox") === "true";
  const loginBaseUrl = isSandbox ? SANDBOX_LOGIN_URL : DEFAULT_LOGIN_URL;
  const loginUrl = `${loginBaseUrl}${LOGIN_PATH}`;

  const authUrl =
    clientId &&
    `${loginUrl}?response_type=token&client_id=${encodeURIComponent(clientId)}&redirect_uri=${encodeURIComponent(
      redirectUri
    )}`;

  const urlToOpen = authUrl || loginBaseUrl;
  const dialogOptions: Office.DialogOptions = { height: 60, width: 30 };

  Office.context.ui.displayDialogAsync(urlToOpen, dialogOptions, (asyncResult: Office.AsyncResult<Office.Dialog>) => {
    if (asyncResult.status === Office.AsyncResultStatus.Failed) {
      console.error("Error displaying dialog:", asyncResult.error?.message);
      notifyIframe({ type: "AUTH_POPUP_CLOSED" });
      return;
    }

    const dialog = asyncResult.value;
    dialog.addEventHandler(Office.EventType.DialogMessageReceived, (arg) => {
      if ("error" in arg) return;
      try {
        const message = JSON.parse(arg.message);
        if (message.status === "success") {
          notifyIframe({ type: "AUTH_SUCCESS", token: message.token, instanceUrl: message.instanceUrl });
          dialog.close();
        } else {
          notifyIframe({ type: "AUTH_POPUP_CLOSED" });
          dialog.close();
        }
      } catch {
        notifyIframe({ type: "AUTH_POPUP_CLOSED" });
        dialog.close();
      }
    });

    dialog.addEventHandler(Office.EventType.DialogEventReceived, () => {
      notifyIframe({ type: "AUTH_POPUP_CLOSED" });
    });
  });
}
